'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Search, X, Phone, Users, Calendar, BarChart2, Settings, FileText, Loader2 } from 'lucide-react';

import { useTranslation } from '@/context/LanguageContext';


interface SearchResult {
  id: string;
  type: 'patient' | 'call' | 'appointment' | 'page';
  title: string;
  subtitle?: string;
  href: string;
  icon: React.ElementType;
}

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

export default function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { t } = useTranslation();

  const STATIC_PAGES: SearchResult[] = [
    { id: 'p-dashboard', type: 'page', title: t('globalSearch.pages.dashboardTitle'), subtitle: t('globalSearch.pages.dashboardSubtitle'), href: '/call-dashboard', icon: BarChart2 },
    { id: 'p-patients', type: 'page', title: t('globalSearch.pages.patientsTitle'), subtitle: t('globalSearch.pages.patientsSubtitle'), href: '/patient-management', icon: Users },
    { id: 'p-calendar', type: 'page', title: t('globalSearch.pages.calendarTitle'), subtitle: t('globalSearch.pages.calendarSubtitle'), href: '/calendar', icon: Calendar },
    { id: 'p-calls', type: 'page', title: t('globalSearch.pages.callsTitle'), subtitle: t('globalSearch.pages.callsSubtitle'), href: '/call-history', icon: Phone },
    { id: 'p-analytics', type: 'page', title: t('globalSearch.pages.analyticsTitle'), subtitle: t('globalSearch.pages.analyticsSubtitle'), href: '/analytics', icon: BarChart2 },
    { id: 'p-reports', type: 'page', title: t('globalSearch.pages.reportsTitle'), subtitle: t('globalSearch.pages.reportsSubtitle'), href: '/reports', icon: FileText },
    { id: 'p-settings', type: 'page', title: t('globalSearch.pages.settingsTitle'), subtitle: t('globalSearch.pages.settingsSubtitle'), href: '/settings', icon: Settings },
  ];

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(STATIC_PAGES.slice(0, 6));
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults(STATIC_PAGES.slice(0, 6));
      return;
    }
    setLoading(true);
    try {
      const [patientsRes, callsRes] = await Promise.allSettled([
        fetch(`/api/patients?q=${encodeURIComponent(q)}&perPage=5`),
        fetch(`/api/calls/history?q=${encodeURIComponent(q)}&perPage=5`),
      ]);

      const combined: SearchResult[] = [];

      if (patientsRes.status === 'fulfilled' && patientsRes.value.ok) {
        const json = await patientsRes.value.json();
        const patients = json.data ?? json.patients ?? [];
        patients.slice(0, 4).forEach((p: any) => {
          combined.push({
            id: `patient-${p.id}`,
            type: 'patient',
            title: p.name,
            subtitle: p.phone ?? p.email ?? t('globalSearch.types.patient'),
            href: `/patients/${p.id}`,
            icon: Users,
          });
        });
      }

      if (callsRes.status === 'fulfilled' && callsRes.value.ok) {
        const json = await callsRes.value.json();
        const calls = json.data ?? json.calls ?? [];
        calls.slice(0, 3).forEach((c: any) => {
          combined.push({
            id: `call-${c.id}`,
            type: 'call',
            title: c.patientName ?? c.callerNumber ?? t('globalSearch.types.call'),
            subtitle: new Date(c.createdAt).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
            href: `/calls/${c.id}`,
            icon: Phone,
          });
        });
      }

      const pageMatches = STATIC_PAGES.filter(p =>
        p.title.toLowerCase().includes(q.toLowerCase()) ||
        (p.subtitle ?? '').toLowerCase().includes(q.toLowerCase())
      );

      setResults([...combined, ...pageMatches].slice(0, 8));
    } catch {
      setResults(STATIC_PAGES.filter(p => p.title.toLowerCase().includes(q.toLowerCase())));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (result: SearchResult) => {
    router.push(result.href);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(s => Math.min(s + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(s => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selected]) handleSelect(results[selected]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('globalSearch.ariaLabel')}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          {loading ? (
            <Loader2 size={18} className="text-muted-foreground animate-spin shrink-0" />
          ) : (
            <Search size={18} className="text-muted-foreground shrink-0" aria-hidden="true" />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('globalSearch.placeholder')}
            className="flex-1 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
            aria-label={t('globalSearch.ariaLabel')}
            aria-autocomplete="list"
            aria-controls="search-results"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label={t('globalSearch.clearSearch')} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-1 px-1.5 py-0.5 bg-muted rounded text-[10px] text-muted-foreground font-mono">Esc</kbd>
        </div>

        {/* Results */}
        <ul id="search-results" role="listbox" className="py-2 max-h-80 overflow-y-auto">
          {results.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t('globalSearch.noResults')} &ldquo;{query}&rdquo;
            </li>
          ) : (
            results.map((result, i) => {
              const ResultIcon = result.icon;
              return (
                <li key={result.id} role="option" aria-selected={i === selected}>
                  <button
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelected(i)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      i === selected ? 'bg-primary/10' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      result.type === 'patient' ? 'bg-blue-100 text-blue-600' :
                      result.type === 'call'? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
                    }`}>
                      <ResultIcon size={15} aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{result.title}</p>
                      {result.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                      {t(`globalSearch.types.${result.type}`, result.type)}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>

        {/* Footer hints */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-border bg-muted/30">
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white border border-border rounded text-[10px] font-mono">↑↓</kbd> {t('globalSearch.navigate')}
          </span>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white border border-border rounded text-[10px] font-mono">↵</kbd> {t('globalSearch.open')}
          </span>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white border border-border rounded text-[10px] font-mono">Esc</kbd> {t('globalSearch.close')}
          </span>
        </div>
      </div>
    </div>
  );
}
